import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, Control, FieldErrors, useWatch } from "react-hook-form";
import { TransferAccountOption, TransferMoneyFormValues } from "../model/types";

type TransferAccountsSectionProps = {
  control: Control<TransferMoneyFormValues>;
  errors: FieldErrors<TransferMoneyFormValues>;
  fields: Array<{ id: string }>;
  append: (value: TransferMoneyFormValues["accountsTo"][number]) => void;
  remove: (index: number) => void;
  accountToOptions: TransferAccountOption[];
  accountToBalances: Record<
    number,
    {
      balance: number;
      currency: string;
    } | null
  >;
  accountToBalanceLoading: Record<number, boolean>;
};

export const TransferAccountsSection = ({
  control,
  errors,
  fields,
  append,
  remove,
  accountToOptions,
  accountToBalances,
  accountToBalanceLoading,
}: TransferAccountsSectionProps) => {
  const accountsTo = useWatch({
    control,
    name: "accountsTo",
  });

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">
        Выберите аккаунт для зачисления
      </Typography>

      {fields.map((field, index) => {
        const selectedCurrency = accountsTo?.[index]?.currency;
        const selectedAccount = accountsTo?.[index]?.accountTo;
        const selectedAccountId = selectedAccount?.psaaId;

        const accountToBalanceInfo =
          selectedAccountId != null
            ? accountToBalances[selectedAccountId]
            : null;

        const isBalanceLoading =
          selectedAccountId != null
            ? !!accountToBalanceLoading[selectedAccountId]
            : false;

        const selectedValues =
          accountsTo
            ?.map((item, itemIndex) =>
              itemIndex === index ? null : item?.accountTo?.value ?? null,
            )
            .filter(Boolean) ?? [];

        const filteredOptions = accountToOptions.filter((option) => {
          const currentValue = accountsTo?.[index]?.accountTo?.value;
          if (option.value === currentValue) return true;

          return !selectedValues.includes(option.value);
        });

        return (
          <Box
            key={field.id}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body1" fontWeight={600}>
                  Получатель {index + 1}
                </Typography>

                {fields.length > 1 && (
                  <IconButton onClick={() => remove(index)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                )}
              </Stack>

              <Controller
                name={`accountsTo.${index}.accountTo`}
                control={control}
                rules={{ required: "Выберите аккаунт назначения" }}
                render={({ field: controllerField, fieldState }) => (
                  <Autocomplete
                    value={controllerField.value}
                    onChange={(_, option) => {
                      controllerField.onChange(option);
                    }}
                    options={filteredOptions}
                    getOptionLabel={(option) => option.label}
                    getOptionDisabled={(option) => !!option.isDisabled}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    renderOption={(props, option) => {
                      // eslint-disable-next-line react/prop-types
                      const { key, ...optionProps } = props;

                      return (
                        <Box
                          component="li"
                          key={key}
                          {...optionProps}
                          sx={{
                            opacity: option.isDisabled ? 0.65 : 1,
                          }}
                        >
                          {option.label}
                          {option.isDisabled && option.disabledReason ? (
                            <Box
                              component="span"
                              sx={{ color: "warning.main", ml: 0.5 }}
                            >
                              {" "}
                              ({option.disabledReason})
                            </Box>
                          ) : null}
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Аккаунт назначения"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />

              {selectedAccount && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Остаток на аккаунте:{" "}
                    {isBalanceLoading ? (
                      <CircularProgress size={14} />
                    ) : accountToBalanceInfo ? (
                      <b>
                        {accountToBalanceInfo.balance}{" "}
                        {accountToBalanceInfo.currency}
                      </b>
                    ) : (
                      <b>Не удалось загрузить</b>
                    )}
                  </Typography>
                </Box>
              )}

              <Controller
                name={`accountsTo.${index}.sum`}
                control={control}
                rules={{
                  required: "Введите сумму",
                  validate: (value) => {
                    const numberValue = Number(value);
                    if (Number.isNaN(numberValue) || numberValue <= 0) {
                      return "Сумма должна быть больше 0";
                    }

                    return true;
                  },
                }}
                render={({ field: controllerField, fieldState }) => (
                  <TextField
                    {...controllerField}
                    label="Сумма перевода"
                    type="text"
                    inputProps={{ inputMode: "decimal" }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <TextField
                value={selectedCurrency ?? ""}
                label="Валюта"
                disabled
              />
            </Stack>
          </Box>
        );
      })}

      <Box>
        <Button
          variant="text"
          onClick={() =>
            append({
              accountTo: null,
              sum: "",
              currency: null,
            })
          }
          disabled={accountToOptions.length <= fields.length}
        >
          Добавить еще
        </Button>
      </Box>

      {!!errors.accountsTo &&
        typeof errors.accountsTo?.message === "string" && (
          <Typography variant="body2" color="error">
            {errors.accountsTo.message}
          </Typography>
        )}
    </Stack>
  );
};
